import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Badge, Card, DataState, PageHeader } from '@/components/Common'
import { CheckField, Field, FormSection, TextArea, TextField } from '@/components/Form'
import { ConfirmDialog, Modal } from '@/components/Modal'
import { FilterEditor } from '@/pages/Filters'
import { useAuth } from '@/lib/auth'
import { useFetch } from '@/lib/hooks'
import { useResource } from '@/lib/resource'
import type {
  Device,
  FacilityTree,
  Reference,
  Source,
  Worklist,
  WorklistPreview,
} from '@/lib/api'

const EMPTY = {
  name: '',
  description: '',
  source_id: 0,
  facility_ids: [] as number[],
  department_ids: [] as number[],
  start_offset_days: -1,
  end_offset_days: 7,
  sps_status_filter: 'SCHEDULED,ARRIVED,READY,STARTED',
  max_results: 100,
  enabled: true,
  default_for_unmatched: false,
  // 'caller' by default on a NEW worklist: the receiving AE title is the
  // station, and it is what makes a device that filters on its own title work.
  station_ae_mode: 'caller' as 'data' | 'caller' | 'fixed',
  station_ae_value: '',
  ignore_caller_date: false,
  ae_titles: [] as string[],
  notes: '',
}

type Draft = typeof EMPTY

export function Worklists() {
  const worklists = useResource<Worklist>('/api/v1/worklists')
  const sources = useFetch<Source[]>('/api/v1/sources')
  const tenancy = useFetch<FacilityTree[]>('/api/v1/tenancy')
  const devices = useFetch<Device[]>('/api/v1/devices')
  const reference = useFetch<Reference>('/api/v1/system/reference')
  const { can } = useAuth()

  const [editing, setEditing] = useState<Worklist | 'new' | null>(null)
  const [deleting, setDeleting] = useState<Worklist | null>(null)
  const [filtersFor, setFiltersFor] = useState<Worklist | null>(null)
  const [resultsFor, setResultsFor] = useState<Worklist | null>(null)

  const mayEdit = can('worklist_admin')
  const list = worklists.data ?? []

  return (
    <>
      <PageHeader
        title="Worklists"
        subtitle="A named, filtered view of one source — the thing a modality is given."
        actions={
          mayEdit && (
            <button className="btn btn-primary" onClick={() => setEditing('new')}>
              Add worklist
            </button>
          )
        }
      />

      {worklists.saveError && (
        <div className="notice notice-error">{worklists.saveError}</div>
      )}

      <Card>
        <p className="card-note">
          A worklist is a <strong>selection of records</strong>, and the AE
          titles are <strong>who receives it</strong>. That association is the
          whole configuration for most sites — build the list, tick the
          scanners. <Link to="/rules">Rules</Link> exist for what a title list
          cannot express (a whole subnet, the caller&rsquo;s own facility) and
          most sites never need one.
        </p>

        <DataState
          loading={worklists.loading}
          error={worklists.error}
          empty={list.length === 0}
          emptyMessage="No worklists yet. Add a source first, then a worklist that reads from it."
        />

        {list.length > 0 && (
          <div className="table-wrap">
            <table className="ae-table">
              <thead>
                <tr>
                  <th>Worklist</th>
                  <th>Goes to (AE titles)</th>
                  <th>Source</th>
                  <th>Window</th>
                  <th>Statuses</th>
                  <th>Covers</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {list.map((worklist) => (
                  <tr key={worklist.id} className={worklist.enabled ? undefined : 'row-muted'}>
                    <td>
                      <button
                        className="link-button"
                        onClick={() => setResultsFor(worklist)}
                        title="Show what this worklist returns right now"
                      >
                        <strong>{worklist.name}</strong>
                      </button>
                      {!worklist.enabled && <> <Badge state="DISABLED" /></>}
                      {worklist.description && (
                        <div className="hint">{worklist.description}</div>
                      )}
                      {worklist.filters.length > 0 && (
                        <div className="hint">
                          {worklist.filters.length} filter
                          {worklist.filters.length === 1 ? '' : 's'}
                          {worklist.filters.some((f) => !f.resolvable) && (
                            <span className="warn-text"> — one cannot be resolved</span>
                          )}
                        </div>
                      )}
                    </td>
                    <td>
                      {worklist.station_ae_mode !== 'data' && (
                        <div className="hint">
                          station:{' '}
                          {worklist.station_ae_mode === 'caller'
                            ? 'each receiving AE title'
                            : worklist.station_ae_value}
                        </div>
                      )}
                      {worklist.ae_titles.length === 0 ? (
                        worklist.default_for_unmatched ? (
                          <span className="tag">default for unmatched callers</span>
                        ) : worklist.rule_count > 0 ? (
                          <span className="hint">
                            none — reached by {worklist.rule_count} rule
                            {worklist.rule_count === 1 ? '' : 's'}
                          </span>
                        ) : (
                          <span className="warn-text">
                            nobody — this worklist is unreachable
                          </span>
                        )
                      ) : (
                        <>
                          {worklist.ae_titles.map((a) => (
                            <span
                              key={a.id}
                              className={a.known_devices === 0 ? 'tag perm-alert' : 'tag'}
                              title={
                                a.known_devices === 0
                                  ? 'No enabled device is registered under this AE title — check for a typo'
                                  : `${a.known_devices} registered device(s)`
                              }
                            >
                              {a.ae_title}
                              {a.known_devices === 0 && ' ?'}
                            </span>
                          ))}
                          {worklist.default_for_unmatched && (
                            <div className="hint">also the unmatched default</div>
                          )}
                        </>
                      )}
                    </td>
                    <td>
                      {worklist.source_name}
                      <div className="hint">{worklist.source_kind}</div>
                      {!worklist.source_enabled && (
                        <div className="warn-text">source disabled</div>
                      )}
                    </td>
                    <td className="mono">
                      {formatWindow(worklist.start_offset_days, worklist.end_offset_days)}
                    </td>
                    <td className="hint">{worklist.sps_status_filter || 'all'}</td>
                    <td>
                      {worklist.facilities.length === 0 &&
                      worklist.departments.length === 0 ? (
                        <span className="hint">every facility</span>
                      ) : (
                        <>
                          {worklist.facilities.map((f) => (
                            <span key={f.id} className="tag" title={f.name}>
                              {f.code}
                            </span>
                          ))}
                          {worklist.departments.map((d) => (
                            <span key={`d${d.id}`} className="tag" title={d.name}>
                              {d.facility_code}/{d.code}
                            </span>
                          ))}
                        </>
                      )}
                    </td>
                    <td className="actions">
                      <button
                        className="btn btn-small"
                        onClick={() => setResultsFor(worklist)}
                      >
                        Results
                      </button>
                      <button className="btn btn-small" onClick={() => setFiltersFor(worklist)}>
                        Filters
                      </button>
                      {mayEdit && (
                        <>
                          <button className="btn btn-small" onClick={() => setEditing(worklist)}>
                            Edit
                          </button>
                          <button
                            className="btn btn-small btn-danger-quiet"
                            onClick={() => setDeleting(worklist)}
                          >
                            Delete
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {list.some(
          (w) => w.enabled && w.ae_titles.length === 0 && w.rule_count === 0 &&
                 !w.default_for_unmatched,
        ) && (
          <div className="notice notice-warn">
            A worklist with no AE titles, no rule and not set as the default is
            unreachable — nothing can serve it.
          </div>
        )}
        {list.some((w) => w.ae_titles.some((a) => a.known_devices === 0)) && (
          <div className="notice notice-warn">
            A title marked <strong>?</strong> has no enabled device registered
            under it. That is fine for a scanner not yet commissioned, and is a
            typo otherwise — the association matches on the name a device calls
            itself, so a wrong name silently receives nothing.
          </div>
        )}
      </Card>

      {editing && (
        <WorklistEditor
          worklist={editing === 'new' ? null : editing}
          sources={sources.data ?? []}
          tenancy={tenancy.data ?? []}
          devices={devices.data ?? []}
          statuses={reference.data?.sps_statuses ?? []}
          saving={worklists.saving}
          error={worklists.saveError}
          onClose={() => {
            setEditing(null)
            worklists.clearError()
          }}
          onSave={async (draft) => {
            const saved =
              editing === 'new'
                ? await worklists.create(draft)
                : await worklists.update(editing.id, draft)
            if (saved) setEditing(null)
          }}
        />
      )}

      {resultsFor && (
        <WorklistResults worklist={resultsFor} onClose={() => setResultsFor(null)} />
      )}

      {filtersFor && (
        <FilterEditor
          ownerKind="worklist"
          ownerId={filtersFor.id}
          ownerName={filtersFor.name}
          reference={reference.data ?? null}
          onClose={() => {
            setFiltersFor(null)
            worklists.reload()
          }}
        />
      )}

      {deleting && (
        <ConfirmDialog
          title={`Delete worklist '${deleting.name}'?`}
          confirmLabel="Delete worklist"
          destructive
          busy={worklists.saving}
          error={worklists.saveError}
          onCancel={() => setDeleting(null)}
          onConfirm={async () => {
            if (await worklists.remove(deleting.id)) setDeleting(null)
          }}
        >
          <p>Its filters go with it.</p>
          {deleting.rule_count > 0 && (
            <div className="notice notice-error">
              {deleting.rule_count} rule(s) still serve this worklist. The delete
              will be refused — repoint or remove them first.
            </div>
          )}
        </ConfirmDialog>
      )}
    </>
  )
}

function formatWindow(start: number, end: number): string {
  const day = (n: number) =>
    n === 0 ? 'today' : n === -1 ? 'yesterday' : n === 1 ? 'tomorrow' : `${n > 0 ? '+' : ''}${n}d`
  return start === end ? day(start) : `${day(start)} → ${day(end)}`
}

function WorklistEditor({
  worklist,
  sources,
  tenancy,
  devices,
  statuses,
  saving,
  error,
  onClose,
  onSave,
}: {
  worklist: Worklist | null
  sources: Source[]
  tenancy: FacilityTree[]
  devices: Device[]
  statuses: string[]
  saving: boolean
  error: string | null
  onClose: () => void
  onSave: (draft: Draft) => void
}) {
  const [draft, setDraft] = useState<Draft>(() =>
    worklist
      ? {
          name: worklist.name,
          description: worklist.description,
          source_id: worklist.source_id,
          facility_ids: worklist.facility_ids,
          department_ids: worklist.department_ids,
          start_offset_days: worklist.start_offset_days,
          end_offset_days: worklist.end_offset_days,
          sps_status_filter: worklist.sps_status_filter,
          max_results: worklist.max_results,
          enabled: worklist.enabled,
          default_for_unmatched: worklist.default_for_unmatched,
          station_ae_mode: worklist.station_ae_mode,
          station_ae_value: worklist.station_ae_value,
          ignore_caller_date: worklist.ignore_caller_date,
          ae_titles: worklist.ae_titles.map((a) => a.ae_title),
          notes: worklist.notes,
        }
      : { ...EMPTY, source_id: sources[0]?.id ?? 0 },
  )
  const [manualTitle, setManualTitle] = useState('')

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((d) => ({ ...d, [key]: value }))

  const selected = new Set(
    draft.sps_status_filter.split(',').map((s) => s.trim()).filter(Boolean),
  )
  const toggleStatus = (status: string, on: boolean) => {
    const next = new Set(selected)
    if (on) next.add(status)
    else next.delete(status)
    set('sps_status_filter', [...next].join(','))
  }

  const span = draft.end_offset_days - draft.start_offset_days

  return (
    <Modal
      title={worklist ? `Edit '${worklist.name}'` : 'Add worklist'}
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="btn btn-primary"
            disabled={saving || !draft.name || !draft.source_id}
            onClick={() => onSave(draft)}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </>
      }
    >
      {error && <div className="notice notice-error">{error}</div>}

      <FormSection
        title="Who gets this worklist"
        tone="outbound"
        description={
          <>
            The AE titles that receive it. This is the primary grouping — for
            most worklists it is the only thing that decides who sees the list.
            Matching is on the name a device calls itself, case-insensitively.
          </>
        }
      >
        {draft.ae_titles.length === 0 && !draft.default_for_unmatched && (
          <div className="notice notice-warn">
            Nobody yet. Until a title is added — or a rule points here, or this
            is set as the unmatched default — no modality can reach this
            worklist.
          </div>
        )}

        <div className="services">
          {[...new Set(devices.filter((d) => d.enabled).map((d) => d.ae_title))]
            .sort()
            .map((title) => {
              const device = devices.find((d) => d.ae_title === title)
              const checked = draft.ae_titles.some(
                (t) => t.toUpperCase() === title.toUpperCase(),
              )
              return (
                <CheckField
                  key={title}
                  label={title}
                  detail={
                    device
                      ? `${device.name}${device.facility_code ? ` · ${device.facility_code}` : ''}`
                      : undefined
                  }
                  checked={checked}
                  onChange={(on) =>
                    set(
                      'ae_titles',
                      on
                        ? [...draft.ae_titles, title]
                        : draft.ae_titles.filter(
                            (t) => t.toUpperCase() !== title.toUpperCase(),
                          ),
                    )
                  }
                />
              )
            })}
          {devices.filter((d) => d.enabled).length === 0 && (
            <span className="hint">
              No devices registered yet. Add them under{' '}
              <Link to="/devices">Devices</Link>, or type a title below.
            </span>
          )}
        </div>

        {/* Titles associated here but not matching any registered device.
            Kept visible and removable: a scanner that is not commissioned yet
            is a legitimate reason to have one, and a typo is the other. */}
        {draft.ae_titles.filter(
          (t) => !devices.some((d) => d.ae_title.toUpperCase() === t.toUpperCase()),
        ).length > 0 && (
          <>
            <div className="subhead">Not matching a registered device</div>
            <div className="services">
              {draft.ae_titles
                .filter(
                  (t) => !devices.some((d) => d.ae_title.toUpperCase() === t.toUpperCase()),
                )
                .map((title) => (
                  <span key={title} className="tag perm-alert">
                    {title}
                    <button
                      className="btn btn-small"
                      onClick={() =>
                        set('ae_titles', draft.ae_titles.filter((t) => t !== title))
                      }
                    >
                      remove
                    </button>
                  </span>
                ))}
            </div>
            <p className="hint">
              Fine for a scanner not yet commissioned. Otherwise it is a typo,
              and a wrong title silently receives nothing.
            </p>
          </>
        )}

        <div className="form-grid">
          <TextField
            label="Add a title not in the list"
            value={manualTitle}
            onChange={setManualTitle}
            maxLength={16}
            showCount
            hint="For a device that is not registered yet. 16 characters, the DICOM limit."
          />
          <div className="filter-actions">
            <button
              className="btn"
              type="button"
              disabled={
                !manualTitle.trim() ||
                draft.ae_titles.some(
                  (t) => t.toUpperCase() === manualTitle.trim().toUpperCase(),
                )
              }
              onClick={() => {
                set('ae_titles', [...draft.ae_titles, manualTitle.trim()])
                setManualTitle('')
              }}
            >
              Add title
            </button>
          </div>
        </div>

        <Field
          label="Scheduled Station AE Title on the results"
          hint="DICOM (0040,0001) — which device a step is booked on. Many modalities send their own AE title as a matching key, so this decides whether they see anything."
        >
          <select
            value={draft.station_ae_mode}
            onChange={(e) =>
              set('station_ae_mode', e.target.value as 'data' | 'caller' | 'fixed')
            }
          >
            <option value="caller">
              the receiving AE title — recommended
            </option>
            <option value="data">whatever the order source supplies</option>
            <option value="fixed">a fixed station name</option>
          </select>
          <span className="field-hint">
            {draft.station_ae_mode === 'caller'
              ? 'Each device sees its own title, and a query filtered on it matches. This is what makes "show me what I am booked for" work.'
              : draft.station_ae_mode === 'data'
                ? 'Only correct if the order feed actually populates the field — if it does not, a device that filters on it sees nothing.'
                : 'Every result carries the same station, whichever device asked.'}
          </span>
        </Field>
        {draft.station_ae_mode === 'fixed' && (
          <TextField
            label="Station name"
            value={draft.station_ae_value}
            onChange={(v) => set('station_ae_value', v)}
            maxLength={16}
            required
          />
        )}

        <CheckField
          label="Serve this to callers nothing else covers"
          detail="At most one worklist can be the default. Without one, an unconfigured scanner gets an empty list and a Success status — which at the modality looks exactly like having no work booked."
          checked={draft.default_for_unmatched}
          onChange={(v) => set('default_for_unmatched', v)}
        />
      </FormSection>

      <FormSection title="Identity">
        <div className="form-grid">
          <TextField label="Name" value={draft.name} onChange={(v) => set('name', v)} required />
          <TextField
            label="Description"
            value={draft.description}
            onChange={(v) => set('description', v)}
          />
        </div>
        <Field label="Source" required hint="Where the scheduled steps are read from.">
          <select
            value={draft.source_id}
            onChange={(e) => set('source_id', Number(e.target.value))}
          >
            {sources.length === 0 && <option value={0}>no sources defined</option>}
            {sources.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name} ({s.kind}){s.enabled ? '' : ' — disabled'}
              </option>
            ))}
          </select>
        </Field>
        <CheckField
          label="Enabled"
          detail="A disabled worklist serves nothing, and any rule pointing at it returns no rows."
          checked={draft.enabled}
          onChange={(v) => set('enabled', v)}
        />
      </FormSection>

      <FormSection
        title="Date window"
        description="Applied on every query and ANDed with what the modality asks for. This is the single most important protective setting on a worklist."
      >
        <div className="form-grid">
          <Field label="From" hint="Days relative to today. -1 is yesterday.">
            <input
              type="number"
              value={draft.start_offset_days}
              onChange={(e) => set('start_offset_days', Number(e.target.value))}
            />
          </Field>
          <Field label="To" hint="7 is next week. 0 and 0 together mean today only.">
            <input
              type="number"
              value={draft.end_offset_days}
              onChange={(e) => set('end_offset_days', Number(e.target.value))}
            />
          </Field>
        </div>
        {span < 0 && (
          <div className="notice notice-error">
            The window ends before it starts, so this worklist could never match
            a row.
          </div>
        )}
        {span > 90 && (
          <div className="notice notice-warn">
            A {span}-day window is wider than the server's ceiling, so the daemon
            will clamp it and log that it did. A wide window is also the fastest
            way to make a worklist query expensive.
          </div>
        )}
        <CheckField
          label="Ignore the date the modality asks for"
          detail="Answer with the whole window above, whatever ScheduledProcedureStepStartDate the device sends. Most modalities ask for today; a test worklist holds orders dated whenever the feed sent them, and this is what stops those two having to agree."
          checked={draft.ignore_caller_date}
          onChange={(v) => set('ignore_caller_date', v)}
        />
        {draft.ignore_caller_date && (
          <div className="notice notice-warn">
            This is the one setting that shows a caller MORE than it asked for. The
            window, status filter, attribute filters and tenancy all still apply,
            and the daemon logs mwl.caller_date_ignored on every query it affects —
            but a device that asked for today will be handed the whole window,
            which some modalities display or sort oddly. Do not set it on a
            worklist serving a live clinical device.
          </div>
        )}

      </FormSection>

      <FormSection
        title="Which steps are visible"
        description="A step that is finished or abandoned is not work. Leaving those on the list is how a technologist scans the same patient twice."
      >
        <div className="services">
          {statuses.map((status) => (
            <CheckField
              key={status}
              label={status}
              checked={selected.has(status)}
              onChange={(on) => toggleStatus(status, on)}
            />
          ))}
        </div>
        <Field
          label="Maximum matches"
          hint="Beyond this the answer is cut short and reported as truncated — never as complete."
        >
          <input
            type="number"
            value={draft.max_results}
            onChange={(e) => set('max_results', Number(e.target.value))}
          />
        </Field>
      </FormSection>

      <FormSection
        title="Which records are in this list"
        description={
          <>
            Leave both empty to cover <strong>every</strong> facility. Tick
            facilities to narrow the list to those sites, and departments to
            narrow it further within them.
          </>
        }
      >
        <div className="subhead">Facilities</div>
        {draft.facility_ids.length === 0 ? (
          <p className="hint">
            None ticked — this worklist covers records from every facility.
          </p>
        ) : (
          <p className="hint">
            {draft.facility_ids.length} of {tenancy.length} facilities.
          </p>
        )}
        <div className="services">
          {tenancy.map((facility) => (
            <CheckField
              key={facility.id}
              label={facility.code}
              detail={facility.name}
              checked={draft.facility_ids.includes(facility.id)}
              onChange={(on) =>
                setDraft((d) => {
                  const facility_ids = on
                    ? [...d.facility_ids, facility.id]
                    : d.facility_ids.filter((x) => x !== facility.id)
                  // Un-ticking a facility drops its departments too: a
                  // department whose facility is not covered contributes no
                  // rows, and the API refuses the combination anyway.
                  const dropped = on
                    ? d.department_ids
                    : d.department_ids.filter(
                        (id) => !facility.departments.some((dep) => dep.id === id),
                      )
                  return { ...d, facility_ids, department_ids: dropped }
                })
              }
            />
          ))}
          {tenancy.length === 0 && (
            <span className="hint">
              No facilities defined. Add them under{' '}
              <Link to="/facilities">Facilities</Link>.
            </span>
          )}
        </div>

        {/* Departments only from facilities that are covered — an empty
            facility set means all, so all departments are offered then. */}
        {tenancy.some(
          (f) =>
            f.departments.length > 0 &&
            (draft.facility_ids.length === 0 || draft.facility_ids.includes(f.id)),
        ) && (
          <>
            <div className="subhead">Departments</div>
            {draft.department_ids.length === 0 && (
              <p className="hint">
                None ticked — every department of the facilities above.
              </p>
            )}
            <div className="services">
              {tenancy
                .filter(
                  (f) =>
                    draft.facility_ids.length === 0 || draft.facility_ids.includes(f.id),
                )
                .flatMap((f) =>
                  f.departments.map((d) => (
                    <CheckField
                      key={d.id}
                      label={`${f.code} / ${d.code}`}
                      detail={d.name}
                      checked={draft.department_ids.includes(d.id)}
                      onChange={(on) =>
                        set(
                          'department_ids',
                          on
                            ? [...draft.department_ids, d.id]
                            : draft.department_ids.filter((x) => x !== d.id),
                        )
                      }
                    />
                  )),
                )}
            </div>
          </>
        )}
      </FormSection>

      <FormSection title="Notes">
        <TextArea
          label="Notes"
          value={draft.notes}
          onChange={(v) => set('notes', v)}
          maxLength={1000}
        />
      </FormSection>
    </Modal>
  )
}

// --- Results -----------------------------------------------------------------
//
// "What is actually IN this list right now?" — the question an operator asks
// straight after "which device gets it".
//
// The rows come from `GET /worklists/{id}/preview`, which shells out to
// `mwl-dicomd --preview`. Nothing here rebuilds the query: the date window,
// status filter, tenancy intersection and per-row residual filters are the
// daemon's, so what is shown is what a modality would receive.

/** The columns worth a table, in the order a technologist reads them. */
const PREVIEW_COLUMNS: { key: string; label: string }[] = [
  { key: 'ScheduledProcedureStepStartTime', label: 'Time' },
  { key: 'PatientName', label: 'Patient' },
  { key: 'PatientID', label: 'MRN' },
  { key: 'PatientBirthDate', label: 'Born' },
  { key: 'PatientSex', label: 'Sex' },
  { key: 'AccessionNumber', label: 'Accession' },
  { key: 'Modality', label: 'Mod' },
  { key: 'ScheduledProcedureStepDescription', label: 'Procedure' },
  { key: 'ScheduledStationAETitle', label: 'Station AE' },
  { key: 'ScheduledProcedureStepStatus', label: 'Status' },
]

/** '153000' -> '15:30', '20260916' -> '2026-09-16'. Blank stays blank. */
function formatDicomTime(value: string | undefined): string {
  if (!value || value.length < 4) return value ?? ''
  return `${value.slice(0, 2)}:${value.slice(2, 4)}`
}

function formatDicomDate(value: string | undefined): string {
  if (!value || value.length !== 8) return value ?? ''
  return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`
}

function WorklistResults({
  worklist,
  onClose,
}: {
  worklist: Worklist
  onClose: () => void
}) {
  // Preview as a specific device when the worklist has titles associated: that
  // is the answer that includes its tenancy scoping, and it is the one that
  // matches what the scanner sees. Blank means "the list itself", which is
  // what somebody configuring a worklist with no titles yet wants.
  const [asDevice, setAsDevice] = useState('')
  const titles = worklist.ae_titles.map((a) => a.ae_title)

  const query = new URLSearchParams({ limit: '200' })
  if (asDevice) query.set('calling_ae', asDevice)
  const preview = useFetch<WorklistPreview>(
    `/api/v1/worklists/${worklist.id}/preview?${query.toString()}`,
  )

  const data = preview.data
  const rows = data?.rows ?? []

  return (
    <Modal
      title={`Results — ${worklist.name}`}
      subtitle="What this worklist returns right now, from the daemon's own query."
      onClose={onClose}
      footer={
        <button className="btn" onClick={onClose}>
          Close
        </button>
      }
    >
      {titles.length > 0 && (
        <div className="form-row">
          <label className="field">
            <span className="field-label">Preview as</span>
            <select value={asDevice} onChange={(e) => setAsDevice(e.target.value)}>
              <option value="">the list itself (no device)</option>
              {titles.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </select>
            <span className="hint">
              Choosing a device applies its tenancy scoping, so this is exactly what
              that scanner would receive.
            </span>
          </label>
        </div>
      )}

      <DataState
        loading={preview.loading}
        error={preview.error}
        empty={!preview.loading && !preview.error && data != null && rows.length === 0}
        emptyMessage={
          data && !data.served
            ? data.reason
            : 'Nothing is booked in this worklist’s window right now. That is what a ' +
              'modality would see: an empty list and a Success status.'
        }
      />

      {data && data.served && rows.length > 0 && (
        <>
          <div className="hint" style={{ marginBottom: '0.5rem' }}>
            {data.count} row{data.count === 1 ? '' : 's'}
            {data.truncated && ' (more exist — showing the first 200)'}
            {' · window '}
            {formatWindow(data.worklist.window_start_days, data.worklist.window_end_days)}
            {data.facility_scope ? (
              <> · scoped to {data.facility_scope}</>
            ) : (
              // Not an error — a worklist covering every facility is a normal
              // single-site configuration — but it is the one thing worth
              // saying out loud on a screen showing patients.
              <> · not scoped to any facility</>
            )}
            {data.department_scope && <> ({data.department_scope})</>}
          </div>
          <div className="table-wrap">
            <table className="ae-table">
              <thead>
                <tr>
                  {PREVIEW_COLUMNS.map((c) => (
                    <th key={c.key}>{c.label}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row, index) => (
                  <tr key={`${row.AccessionNumber ?? ''}-${index}`}>
                    {PREVIEW_COLUMNS.map((c) => {
                      let text = row[c.key] ?? ''
                      if (c.key === 'ScheduledProcedureStepStartTime') {
                        text = formatDicomTime(text)
                      } else if (c.key === 'PatientBirthDate') {
                        text = formatDicomDate(text)
                      }
                      const mono =
                        c.key === 'PatientID' ||
                        c.key === 'AccessionNumber' ||
                        c.key === 'ScheduledProcedureStepStartTime'
                      return (
                        <td key={c.key} className={mono ? 'mono' : undefined}>
                          {text || <span className="hint">—</span>}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </Modal>
  )
}
